/**
 * Placement status values matching the da_framework PlacementStatus enum.
 * Values are lowercase as required by the API.
 */
export const PLACEMENT_STATUS = {
  PENDING: "pending",
  PLACED: "placed",
  OUT_OF_SCOPE: "out_of_scope",
}

/**
 * @typedef {Object} StatusDisplayConfig
 * @property {string} label - Display label for the status
 * @property {string} colorClass - CSS class for the status dot color
 */

/** @type {Object<string, StatusDisplayConfig>} */
export const STATUS_CONFIG = {
  [PLACEMENT_STATUS.PENDING]: { label: "Pending", colorClass: "status-pending" },
  [PLACEMENT_STATUS.PLACED]: { label: "Placed", colorClass: "status-placed" },
  [PLACEMENT_STATUS.OUT_OF_SCOPE]: { label: "Out of Scope", colorClass: "status-out-of-scope" },
}

export const STATUS_OPTIONS = Object.entries(STATUS_CONFIG).map(([value, config]) => ({
  value,
  label: config.label,
}))

export const FILTER_OPTIONS = [
  { value: null, label: "All" },
  ...STATUS_OPTIONS,
]

/** Maximum items per bulk API request (matches backend Marshmallow validation) */
export const BULK_API_LIMIT = 100

/**
 * Allowed file extensions for floor plan uploads
 * @type {string[]}
 */
export const ALLOWED_EXTENSIONS = [".pdf", ".png", ".jpg", ".jpeg"]

/**
 * Maximum file size in bytes (50 MB)
 * @type {number}
 */
export const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024

/**
 * Maximum file size in megabytes
 * @type {number}
 */
export const MAX_FILE_SIZE_MB = 50

/**
 * Accept string for file input element
 * @type {string}
 */
export const ACCEPT_STRING = ".pdf,.png,.jpg,.jpeg"

/**
 * Validates file extension against allowed list.
 * @param {string} filename - The filename to validate
 * @returns {boolean} True if extension is allowed
 */
export function isValidExtension(filename) {
  const lower = filename.toLowerCase()
  const ext = lower.slice(lower.lastIndexOf("."))
  return ALLOWED_EXTENSIONS.includes(ext)
}

/**
 * Validates file size against maximum.
 * @param {number} sizeInBytes - File size in bytes
 * @returns {boolean} True if file size is within limit
 */
export function isValidFileSize(sizeInBytes) {
  return sizeInBytes <= MAX_FILE_SIZE_BYTES
}

/**
 * Returns a validation error message for a file, or null if valid.
 * Checks size first, then type.
 * @param {File} file - File object with name and size properties
 * @returns {string|null} Error message or null if valid
 */
export function getValidationError(file) {
  if (!isValidFileSize(file.size)) {
    return `File "${file.name}" exceeds maximum size of ${MAX_FILE_SIZE_MB} MB`
  }
  if (!isValidExtension(file.name)) {
    return `File "${file.name}" has invalid type. Accepted formats: PDF, PNG, JPG.`
  }
  return null
}

/** Custom MIME type to distinguish file-move drags from desktop file drops */
export const FILE_MOVE_MIME_TYPE = "application/x-ciq-file-move"
